package com.evoapps.evoshift.ui.roster

import com.evoapps.evoshift.domain.util.AppClock
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Insights
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.staffing.StaffingVerdict
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.user.text
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.ui.util.staffingColor
import com.evoapps.evoshift.ui.util.staffingVerdictShortRes
import com.evoapps.evoshift.viewmodels.RosterDetailUiState
import evoshift.composeapp.generated.resources.roster_overview_by_email
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.posture_pick_day
import evoshift.composeapp.generated.resources.roster_overview_availability
import evoshift.composeapp.generated.resources.roster_overview_more
import evoshift.composeapp.generated.resources.roster_overview_no_warnings
import evoshift.composeapp.generated.resources.roster_overview_team
import evoshift.composeapp.generated.resources.roster_overview_title
import evoshift.composeapp.generated.resources.roster_overview_warnings
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.pluralStringResource
import androidx.compose.runtime.remember
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.math.roundToInt

/** At most this many days to check in the overview; the rest are counted. */
private const val MaxAlerts = 6

/** Test tag of the roster's overview pane (wide windows, no day chosen). */
const val ROSTER_OVERVIEW_TAG = "roster_overview_pane"

/** One day the overview warns about: what the day lacks, or its staffing verdict. */
internal data class RosterDayAlert(val date: LocalDate, val warning: com.evoapps.evoshift.domain.model.DayStaffAlert?, val verdict: StaffingVerdict?)

/** The month's days with a staffing warning or a staffing hint outside the band, in date order. */
internal fun RosterDetailUiState.dayAlerts(): List<RosterDayAlert> {
    val verdicts = staffing?.days.orEmpty()
        .filter { it.verdict != StaffingVerdict.JUST_RIGHT }
        .associate { it.date to it.verdict }
    return (dayWarnings.keys + verdicts.keys).sorted()
        .map { date -> RosterDayAlert(date, dayWarnings[date], verdicts[date]) }
}

/**
 * SH-105: beside the grid on expanded and wider windows, while no day is chosen: the month's
 * staffing warnings (a day to open), the team's planned hours against their contracts and how
 * many sent their availability. Choosing a day replaces it with the day's shifts.
 */
@Composable
internal fun RosterOverviewPane(
    state: RosterDetailUiState,
    onDayClick: (LocalDate) -> Unit,
    modifier: Modifier = Modifier
) {
    val muted = Color.White.copy(alpha = 0.7f)
    Column(modifier.background(DarkMetallicBackgroundBrush).testTag(ROSTER_OVERVIEW_TAG)) {
        Row(
            Modifier.fillMaxWidth().padding(start = 20.dp, end = 20.dp, top = 16.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Default.Insights, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(12.dp))
            Text(
                stringResource(Res.string.roster_overview_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = LightGold
            )
        }
        Column(
            Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(start = 20.dp, end = 20.dp, top = 4.dp, bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(stringResource(Res.string.posture_pick_day), style = MaterialTheme.typography.bodySmall, color = muted)

            // The days to look at first.
            SectionTitle(stringResource(Res.string.roster_overview_warnings))
            val alerts = state.dayAlerts()
            if (alerts.isEmpty()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.CheckCircle, contentDescription = null, tint = Color(0xFF51CF66), modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.roster_overview_no_warnings), style = MaterialTheme.typography.bodyMedium, color = muted)
                }
            } else {
                // The days still ahead first (they can be changed); a long month stays short.
                val today = remember { AppClock.todayIn(TimeZone.currentSystemDefault()) }
                val ordered = alerts.filter { it.date >= today } + alerts.filter { it.date < today }
                ordered.take(MaxAlerts).forEach { alert -> DayAlertRow(alert, onClick = { onDayClick(alert.date) }) }
                if (ordered.size > MaxAlerts) {
                    val more = ordered.size - MaxAlerts
                    Text(pluralStringResource(Res.plurals.roster_overview_more, more, more), style = MaterialTheme.typography.bodySmall, color = muted)
                }
            }

            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), modifier = Modifier.padding(vertical = 4.dp))

            // The team: who has how many hours planned, against their contract where it is known.
            SectionTitle(stringResource(Res.string.roster_overview_team))
            val people = state.availableEmployees
            val sent = people.count { state.availabilityByUserId[it.uid] != null }
            Text(
                stringResource(Res.string.roster_overview_availability, sent, people.size),
                style = MaterialTheme.typography.bodySmall,
                color = muted
            )
            val hours = state.plannedHoursByUserId
            people.sortedByDescending { hours[it.uid] ?: 0.0 }.forEach { person ->
                val availability = state.availabilityByUserId[person.uid]
                PersonHoursRow(person, hours[person.uid] ?: 0.0, state.targetHoursByUserId[person.uid], availability != null, availability?.viaEmailLink == true)
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text,
        style = MaterialTheme.typography.labelLarge,
        fontWeight = FontWeight.Bold,
        color = Color.White,
        modifier = Modifier.padding(top = 8.dp)
    )
}

@Composable
private fun DayAlertRow(alert: RosterDayAlert, onClick: () -> Unit) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White.copy(alpha = 0.06f))
            .handCursor()
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(Icons.Default.Warning, contentDescription = null, tint = LightGold, modifier = Modifier.size(16.dp))
        Spacer(Modifier.width(10.dp))
        Column(Modifier.weight(1f)) {
            Text(dayAndDate(alert.date), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = Color.White)
            alert.warning?.let { Text(it.text(), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.75f)) }
        }
        alert.verdict?.let { verdict ->
            Text(
                stringResource(staffingVerdictShortRes(verdict)),
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Bold,
                color = staffingColor(verdict, onDark = true)
            )
        }
    }
}

@Composable
private fun PersonHoursRow(person: User, planned: Double, target: Double?, availabilitySent: Boolean, byEmail: Boolean = false) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), verticalAlignment = Alignment.CenterVertically) {
        Icon(
            Icons.Default.CheckCircle,
            contentDescription = null,
            tint = if (availabilitySent) Color(0xFF51CF66) else Color.White.copy(alpha = 0.2f),
            modifier = Modifier.size(14.dp)
        )
        Spacer(Modifier.width(8.dp))
        Text(
            person.displayName,
            style = MaterialTheme.typography.bodyMedium,
            color = Color.White,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f)
        )
        // SH-173: the month came through the e-mailed link's web form.
        if (byEmail) {
            Text(
                stringResource(Res.string.roster_overview_by_email),
                style = MaterialTheme.typography.labelSmall,
                color = Color.White.copy(alpha = 0.6f),
                modifier = Modifier.padding(horizontal = 6.dp)
            )
        }
        val over = target != null && planned > target + 0.01
        Text(
            if (target != null) "${planned.roundToInt()} / ${target.roundToInt()} h" else "${planned.roundToInt()} h",
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            color = if (over) Color(0xFFFF6B6B) else LightGold
        )
    }
}
